import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CopyTextButton } from '../world/CopyTextButton';
import { ItemThumb } from '../world/ItemSprite';
import { characterClasses } from '../../data/guide/classes';
import { classBuildPath, getClassBuild } from '../../data/guide/classBuilds';
import { staffItemImage } from '../../data/guide/itemImages';
import { spriteForItem, watchSheet } from '../../utils/itemSprites';
import { itemPath } from '../../utils/worldFormat';
import { resolveSkillLink, STATUS_LABEL } from '../../utils/skillLinks';
import { PUBLIC_SITE_URL as SITE_ORIGIN_FOR_COPY } from '../../data/site';
import type { SheetStatus } from '../../utils/itemSprites';
import type { SkillLinkStatus } from '../../utils/skillLinks';
import type { WorldData } from '../../types/world';
type Row = {
  cls: string;
  skill: string;
  skillId?: number;
  status: SkillLinkStatus;
  itemId?: string;
  itemName?: string;
  image: 'staff' | 'lamina' | 'ninguna';
  sheetUrl?: string;
  link: string;
  problems: string[];
};
const LOAD_LABEL: Record<SheetStatus | 'staff' | 'ninguna', string> = {
  loading: 'cargando…',
  ok: 'carga comprobada',
  error: 'NO CARGA',
  staff: 'imagen del staff (carga no medida aquí)',
  ninguna: '—'
};

/** Auditoría (solo vista del staff): habilidad → ítem → imagen asignada → carga real de su lámina. */
export function SkillLinkAudit({
  w
}: {
  w: WorldData;
}) {
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    characterClasses.forEach(c => {
      const guide = getClassBuild(c.id);
      guide?.skills.forEach(s => {
        const l = resolveSkillLink(w, c, s.name);
        const ci = l.learn[0] ?? l.origin[0];
        const sprite = ci ? spriteForItem(ci.id) : undefined;
        const image: Row['image'] = ci && staffItemImage(ci.id) ? 'staff' : sprite ? 'lamina' : 'ninguna';
        out.push({
          cls: c.base,
          skill: s.name,
          skillId: l.def?.skillId,
          status: l.status,
          itemId: ci?.id,
          itemName: ci?.name,
          image,
          sheetUrl: image === 'lamina' ? sprite?.url : undefined,
          link: classBuildPath(c.id, undefined, 'habilidades'),
          problems: l.problems
        });
      });
    });
    return out;
  }, [w]);
  const urls = useMemo(() => Array.from(new Set(rows.map(r => r.sheetUrl).filter(Boolean) as string[])), [rows]);
  const [load, setLoad] = useState<Record<string, SheetStatus>>({});
  useEffect(() => {
    const offs = urls.map(u => watchSheet(u, s => setLoad(p => p[u] === s ? p : {
      ...p,
      [u]: s
    })));
    return () => offs.forEach(f => f());
  }, [urls]);
  const loadOf = (r: Row) => r.image === 'lamina' && r.sheetUrl ? LOAD_LABEL[load[r.sheetUrl] ?? 'loading'] : LOAD_LABEL[r.image === 'staff' ? 'staff' : 'ninguna'];
  const counts = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] ?? 0) + 1;
    return acc;
  }, {});
  const failed = rows.filter(r => r.sheetUrl && load[r.sheetUrl] === 'error').length;
  const text = [`AUDITORÍA HABILIDADES · Clases y puntos · ${rows.length} habilidades`, ...Object.entries(counts).map(([k, n]) => `${STATUS_LABEL[k as SkillLinkStatus]}: ${n}`), `Imagen asignada que no carga: ${failed}`, '', 'clase | habilidad | skill_id | item_id | ítem | imagen | carga | estado | enlace', ...rows.map(r => [r.cls, r.skill, r.skillId ?? '—', r.itemId ?? '—', r.itemName ?? '—', r.image, loadOf(r), STATUS_LABEL[r.status], `${SITE_ORIGIN_FOR_COPY}${r.link}`].join(' | ') + (r.problems.length ? `\n   ↳ ${r.problems.join(' · ')}` : ''))].join('\n');
  return <section aria-labelledby="skill-audit-t" className="border-t border-white/10 pt-8" data-id="element-386" data-mp="3jjp8xbi7kchh">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-387" data-mp="3engszvw6sf99">
        <div data-id="element-388" data-mp="1p8k1rx7wxhxe">
          <h2 id="skill-audit-t" className="font-cinzel text-lg font-bold text-white" data-id="element-389" data-mp="1p80tl882e7wk">
            Staff · Auditoría de habilidades e imágenes
          </h2>
          <p className="text-xs text-gray-500" data-id="element-390" data-mp="bc90a3jtpp9d">Solo visible en la vista previa del staff. La carga se mide en este navegador al abrir la página.</p>
        </div>
        <CopyTextButton text={text} label="Copiar auditoría de habilidades" data-id="element-391" data-mp="gcn75i9out5t" />
      </div>
      <p className="mt-3 text-sm text-gray-300" data-id="element-392" data-mp="l7sr4bjj5xr9">
        {Object.entries(counts).map(([k, n]) => `${STATUS_LABEL[k as SkillLinkStatus]}: ${n}`).join(' · ')}{' '}
        · Imagen que no carga: {failed}
      </p>
      <div className="mt-4 overflow-x-auto" data-id="element-393" data-mp="3tpgrs2hb6xbc">
        <table className="w-full min-w-[46rem] text-left text-xs" data-id="element-394" data-mp="3tq8d8kh6zenw">
          <thead className="text-gray-500" data-id="element-395" data-mp="1lo0icseyeov">
            <tr data-id="element-396" data-mp="1kmmbzg3pc2x">
              {['Clase', 'Habilidad', 'skill_id', 'Ítem', 'Imagen', 'Carga', 'Estado'].map(h => <th key={h} className="py-2 pr-3 font-semibold" data-id="element-397" data-mp="6eh3av4bncyb">
                  {h}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 text-gray-300" data-id="element-398" data-mp="3a0eqiktc3pqj">
            {rows.map(r => <tr key={`${r.cls}-${r.skill}`} data-id="element-399" data-mp="32hopbgkof12b">
                <td className="py-2 pr-3" data-id="element-400" data-mp="2xj3xo5jhppph">{r.cls}</td>
                <td className="py-2 pr-3" data-id="element-401" data-mp="2xjn5gtffexps">
                  <Link to={r.link} className="text-white hover:text-[#D4AF37]" data-id="element-402" data-mp="2thiigdjhgzc4">
                    {r.skill}
                  </Link>
                </td>
                <td className="py-2 pr-3 font-mono" data-id="element-403" data-mp="2ohxe1nljnys0">{r.skillId ?? '—'}</td>
                <td className="py-2 pr-3" data-id="element-404" data-mp="2jke2ivg8e03r">
                  {r.itemId ? <span className="flex items-center gap-2" data-id="element-405" data-mp="2es45kvgp7zrz">
                      <ItemThumb id={r.itemId} name={r.itemName ?? r.itemId} size="thumb" focusable={false} placeholder data-id="element-406" data-mp="2eqjb15udwgu4" />
                      <Link to={itemPath(r.itemId)} className="hover:text-[#D4AF37]" data-id="element-407" data-mp="25rxsvbstr3tv">
                        {r.itemName} <span className="font-mono text-gray-500" data-id="element-408" data-mp="20sl9x252qgwk">{r.itemId}</span>
                      </Link>
                    </span> : '—'}
                </td>
                <td className="py-2 pr-3" data-id="element-409" data-mp="1m5waf0ato3q6">{r.image}</td>
                <td className={`py-2 pr-3 ${r.sheetUrl && load[r.sheetUrl] === 'error' ? 'text-red-400' : ''}`} data-id="element-410" data-mp="1m8ijx5h9wtb1">{loadOf(r)}</td>
                <td className="py-2 pr-3" data-id="element-411" data-mp="1c8hwb7cdfr11">
                  {STATUS_LABEL[r.status]}
                  {r.problems.map(p => <span key={p} className="block text-amber-300/90" data-id="element-412" data-mp="1h7uj5s38sy3m">
                      {p}
                    </span>)}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </section>;
}